/**
 * Classify why a bedbank enquiry for an applied loyalty package upgrade failed.
 *
 * Bedbank marks a loyalty-discount rejection with `validWithoutDiscount` when the enquiry carries a
 * discount. If the discount clamps to zero, the flag is absent and classification compares prices.
 */

const PRICE_COMPARISON_TOLERANCE = 0.01

export type UpgradeFailureCause =
  /** The loyalty package discount breaches margin; the target still sells at its own price. */
  | 'upgrade-margin'
  /** The price moved enough to fail with or without the loyalty package discount. */
  | 'price-moved-regardless'
  /** The target can't be booked at any price: sold out, or blocklisted after repeated errors. */
  | 'target-unavailable'
  | 'unrelated'

export type EnquiryFailure =
  | { type: 'price-change', price?: number, luxplusPrice?: number, validWithoutDiscount?: boolean }
  | { type: 'error', error: unknown }

interface UpgradeFailureContext {
  hasUpgradeApplied: boolean
  /** The target rate's own selling price, on the same basis the enquiry used. */
  nonDiscountedPrice: number
  isLuxPlusSubscriber: boolean
}

const UNAVAILABLE_CODES = new Set(['sold_out', 'rate_unavailable'])

function getErrorCodes(error: unknown): Array<string> {
  const errors = (error as { errors?: Array<{ code?: string }> } | undefined)?.errors
  if (!Array.isArray(errors)) return []
  return errors.map(({ code }) => code).filter((code): code is string => !!code)
}

/**
 * Check whether an enquiry failed because the rate is sold out or blocklisted. This is independent
 * of loyalty package upgrade state and applies to both source and target rates.
 */
export function hasUnavailableCode(error: unknown): boolean {
  return getErrorCodes(error).some(code => UNAVAILABLE_CODES.has(code))
}

export function resolveUpgradeFailure(
  failure: EnquiryFailure,
  { hasUpgradeApplied, nonDiscountedPrice, isLuxPlusSubscriber }: UpgradeFailureContext,
): UpgradeFailureCause {
  if (!hasUpgradeApplied) return 'unrelated'

  if (failure.type === 'error') {
    // An applied loyalty package upgrade books the target rate, so an unavailable enquiry identifies
    // that target. Use `hasUnavailableCode` when availability must be checked without an applied
    // loyalty package upgrade.
    return hasUnavailableCode(failure.error) ? 'target-unavailable' : 'unrelated'
  }

  // Trust the explicit flag when bedbank returns no price. A property-fee-only refusal can contain
  // `validWithoutDiscount` and fees only, and matched loyalty package rates must have equal fees.
  if (failure.validWithoutDiscount !== undefined) {
    return failure.validWithoutDiscount ? 'upgrade-margin' : 'price-moved-regardless'
  }

  // Bedbank only sends `price` for a member when the public price moved too.
  const newPrice = isLuxPlusSubscriber ? failure.luxplusPrice ?? failure.price : failure.price
  if (newPrice === undefined) return 'price-moved-regardless'

  // The returned price is the lowest we can sell at and still hold margin.
  return newPrice <= nonDiscountedPrice + PRICE_COMPARISON_TOLERANCE
    ? 'upgrade-margin'
    : 'price-moved-regardless'
}
